import React, { useEffect, useState } from 'react';
import { MenuItemLink } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { Box, alpha } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';

import SubMenu from './SubMenu';
import { sidebarColors } from './appBarTheme';

/**
 * A FontAwesome glyph, stored in menuitems.json as a raw <i> tag.
 *
 * The tags are data, not user input -- they ship in the repo -- so injecting
 * them is safe here. An entry with no usable glyph falls back to a small dot
 * rather than rendering an empty box: "Assignments" showed nothing at all,
 * because its stored name was the FontAwesome 4 spelling and index.html loads
 * FontAwesome 6 without the v4 shim. That name is fixed, but the fallback
 * stays, so the next stale glyph degrades instead of disappearing.
 */
const MenuIcon = ({ menuStr }: { menuStr?: string }) => {
    if (!menuStr || !menuStr.trim()) {
        return <CircleIcon sx={{ fontSize: 7, opacity: 0.6 }} />;
    }
    return (
        <Box
            component="span"
            sx={{ fontSize: 14 }}
            dangerouslySetInnerHTML={{ __html: menuStr }}
        />
    );
};

export const MenuContainer = (props: any) => {
    const { onMenuClick, dense, caption, items } = props;
    const location = useLocation();

    const catIcon = items.length > 0 ? items[0].CatIcon : '';

    /**
     * A group opens itself when the current page is one of its entries.
     *
     * Every group used to start closed, so landing anywhere but the dashboard
     * left the sidebar showing no indication of where you were -- you had to
     * open groups until you found the highlighted row.
     */
    const holdsCurrentRoute = items.some(
        (item: any) =>
            item.NavigateUrl && location.pathname.startsWith(item.NavigateUrl)
    );
    const [isOpen, setIsOpen] = useState(holdsCurrentRoute);

    useEffect(() => {
        if (holdsCurrentRoute) setIsOpen(true);
    }, [holdsCurrentRoute]);

    return (
        <SubMenu
            handleToggle={() => setIsOpen(!isOpen)}
            isOpen={isOpen}
            name={caption}
            dense={dense}
            icon={<MenuIcon menuStr={catIcon} />}
        >
            {items &&
                items.map((item: any, index: number) =>
                    item.View === 1 ? (
                        <MenuItemLink
                            key={index}
                            to={item.NavigateUrl}
                            primaryText={item.Module}
                            leftIcon={<MenuIcon menuStr={item.Icon} />}
                            onClick={onMenuClick}
                            dense={dense}
                            /*
                             * Nested under '&&' for the same reason the app bar
                             * is: radiantTheme and nanoTheme both style
                             * RaMenuItemLink themselves, for a light sidebar,
                             * and a single-class sx loses to them.
                             */
                            sx={theme => {
                                const { fg } = sidebarColors(theme);
                                return {
                                    '&&': {
                                        mr: 1,
                                        my: 0.25,
                                        borderRadius: 2,
                                        // radiantTheme gives every menu item a
                                        // 3px white left border and rounds it to
                                        // '0 100px 100px 0', which showed up on
                                        // this band as a white arc down the left
                                        // of each row.
                                        borderLeft: 'none',
                                        color: alpha(fg, 0.82),
                                        transition:
                                            'background-color .15s ease, padding-left .15s ease',
                                        '& .MuiListItemIcon-root': {
                                            minWidth: 30,
                                            color: 'inherit',
                                        },
                                        '& .MuiTypography-root': {
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.3,
                                            fontSize: '0.8125rem',
                                            fontWeight: 500,
                                        },
                                        '&:hover': {
                                            bgcolor: alpha(fg, 0.09),
                                            color: fg,
                                            borderRadius: 2,
                                        },
                                        // The current page. A tinted pill plus a
                                        // weight change, so it is obvious
                                        // without relying on colour alone.
                                        '&.RaMenuItemLink-active': {
                                            bgcolor: alpha(fg, 0.16),
                                            color: fg,
                                            backgroundImage: 'none',
                                            borderLeft: 'none',
                                            borderRadius: 2,
                                            boxShadow: 'none',
                                            '& .MuiTypography-root': {
                                                fontWeight: 700,
                                            },
                                        },
                                    },
                                };
                            }}
                        />
                    ) : null
                )}
        </SubMenu>
    );
};

export default MenuContainer;
